<template>
  <div class="page">
    <div class="page-toolbar bg-white shadow-sm p-3">
      <breadcrumb />
    </div>
    <div
      v-if="!loading"
      class="power--bi__report-page"
    >
      <ValidationObserver
        v-slot="{handleSubmit}"
        ref="observer"
      >
        <b-form @submit.prevent="handleSubmit(onSubmit)">
          <div class="row">
            <div class="col-sm-12 mb-3">
              <b-card
                no-body
                class="table-details"
              >
                <div slot="header">
                  <span>Report Details</span>
                </div>
                <b-card-body>
                  <div class="row">
                    <div class="col">
                      <input-widget
                        :model="tableModel"
                        :field="fields.displayName"
                      />
                    </div>
                    <div class="col">
                      <input-widget
                        :model="tableModel"
                        :field="fields.folderGuid"
                      />
                    </div>
                  </div>
                  <div class="row">
                    <div class="col">
                      <input-widget
                        :model="tableModel"
                        :field="fields.reportUrlEnvironmentParameter"
                      />
                    </div>
                  </div>
                  <vfa-picker
                    v-model="tableModel.reportIcon"
                    :only="onlyIcons"
                    class="icon-select-input"
                  >
                    <template #activator="{ on }">
                      <b-button
                        id="selectModuleIconBtn"
                        variant="warning"
                        @click="on"
                      >
                        Choose Icon
                      </b-button>
                      <font-awesome-icon
                        v-if="tableModel.reportIcon"
                        id="selectModuleIcon"
                        class="selected-icon"
                        :icon="['fas', tableModel.reportIcon]"
                      />
                    </template>
                  </vfa-picker>
                </b-card-body>
              </b-card>
            </div>
          </div>

          <div class="mt-3">
            <b-button
              type="submit"
              variant="primary"
              value="submit"
              class="mr-2"
            >
              <font-awesome-icon :icon="['far', 'save']" />
              Submit
            </b-button>
            <b-button
              type="button"
              variant="primary"
              value="submit"
              class="mr-2"
              @click="saveAndClose"
            >
              <font-awesome-icon :icon="['far', 'save']" />
              Save and Close
            </b-button>
            <b-button
              type="button"
              variant="danger"
              @click="cancelAction"
            >
              <font-awesome-icon :icon="['fas', 'times']" />
              Cancel
            </b-button>
          </div>
        </b-form>
      </ValidationObserver>
    </div>

    <div v-else>
      <content-spinner :show="loading" />
    </div>
  </div>
</template>

<script>

import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../../../../core/components/ContentSpinner';
import Breadcrumb from '../../../../core/components/Breadcrumb';
import InputWidget from '../../../../core/components/complex-table/components/InputWidget';
import {uuid} from 'vue-uuid';

const {
  mapState: dataManagementMapState,
  mapActions: dataManagementMapActions,
} = createNamespacedHelpers('dataManagement');

export default {
  name: 'TableBuilderPowerBiReport',
  components: {
    Breadcrumb,
    ContentSpinner,
    InputWidget,
  },
  data() {
    return {
      moduleGuid: null,
      folderGuid: null,
      tableModel: {},
      tableFolder: {},
      fields: {
        displayName: {
          type: 'text',
          required: true,
          label: 'Name',
          name: 'displayName',
        },
        folderGuid: {
          type: 'select',
          required: true,
          label: 'Folder',
          name: 'folderGuid',
          data: {},
        },
        reportUrlEnvironmentParameter: {
          type: 'text',
          required: true,
          label: 'Power BI Report Environment Variable Name',
          name: 'reportUrlEnvironmentParameter',
        },
        reportIcon: '',
      },
      loading: false,
      onlyIcons: ['solid'],
    };
  },
  computed: {
    ...dataManagementMapState({
      currentTable: state => state.tableData.data,
      powerBiReportEntity: state => state.powerBiReportEntity,
      moduleFolders: state => state.folders.data,
    }),
    isEditMode() {
      return !!this.$route.params.tableUuid;
    },
  },
  methods: {
    ...dataManagementMapActions([
      'getTableData',
      'getFolders',
      'upsertPowerBiReportEntity',
    ]),
    async onSubmit(redirectToModule = false) {

      if (!await this.$refs.observer.validate()) {
        return;
      }
      this.loading = true;
      const params = {
        ...this.tableModel,
        moduleGuid: this.tableFolder.module.moduleGuid,
      };

      const {success, body} = await this.upsertPowerBiReportEntity(
        {
          reportUrl: this.tableModel.reportUrlEnvironmentParameter,
          entityGuid: this.currentTable.entityGuid || uuid.v4(),
          folderGuid: this.folderGuid || this.tableModel.folderGuid,
          moduleGuid: this.moduleGuid,
          displayName: this.tableModel.displayName,
          reportIcon: this.tableModel.reportIcon,
        });

      if (success) {
        this.$toasted.success(`Table "${params.displayName}" was saved`, {
          duration: TOASTER_DURATION,
        });

        if (redirectToModule) {
          await this.$router.push({
            name: 'dataManagementPowerBiReportView',
            params: {
              tableId: this.$route.params.tableUuid || body.entityGuid,
              moduleUuid: params.moduleGuid,
            },
          });
        }
      } else {
        const errorMessage = body.ErrorMessage;
        this.$toasted.error(errorMessage, {
          duration: TOASTER_DURATION,
        });
      }
      this.loading = false;
    },
    async saveAndClose() {
      if (!await this.$refs.observer.validate()) {
        return;
      }
      await this.onSubmit(true);
    },
    cancelAction() {
      this.$router.go(-1);
    },
    async getData() {
      await this.getFolders(this.moduleGuid);
      if (this.isEditMode) {
        await this.getTableData(this.$route.params.tableUuid);
      }
    },
    prepareData() {
      this.prepareFolderFieldOptions();
      this.getCurrentTableFolder();
      if (this.isEditMode) {
        this.prepareTableModel();
      }
    },
    prepareFolderFieldOptions() {
      this.fields.folderGuid.data = {
        options: this.moduleFolders.map(f => ({text: f.folderName, value: f.folderGuid})),
      };
    },
    getCurrentTableFolder() {
      const folderGuid = this.$route.query.folderUuid || this.currentTable.folderGuid;
      this.tableFolder = this.moduleFolders.find(folder => folder.folderGuid === folderGuid);
    },
    prepareTableModel() {
      this.tableModel = {
        displayName: this.currentTable.displayName,
        reportUrlEnvironmentParameter: JSON.parse(this.currentTable.entityConfigJSON).EnvironmentVariableWithPowerBiUrl,
        moduleGuid: this.currentTable.moduleGuid,
        folderGuid: this.currentTable.folderGuid,
        reportIcon: this.currentTable.tableIcon,
      };
    },
  },
  beforeMount() {
    this.moduleGuid = this.$route.params.moduleUuid;
    this.folderGuid = this.$route.query.folderUuid;
    this.tableModel.folderGuid = this.folderGuid;
  },
  async mounted() {
    this.loading = true;
    await this.getData();
    this.prepareData();
    this.loading = false;
  },
};
</script>

<style scoped>
.power--bi__report-page {
  padding: 20px;
}

.icon-select-input {
  display: flex;
  align-items: center;
  gap: 10px;

  & .selected-icon {
    width: 20px;
    height: 20px;
    padding: 5px;
    border: 1px solid #ced4da;
  }
}
</style>